<template>
    <div class="match-info IN_MatchCard_Btn_MatchInfo" v-on:click="goToMatchDetail()">
        <div class="match-info-team">
            <div
                class="match-info-team-name JARVIS_MatchCard_Img_TeamNameA"
                v-bind:class="{
                    _favorite: info.isHomeFavorite,
                    _underdog: !info.isHomeFavorite,
                }"
            >
                <span>{{ homeTeamName }}</span>
                <div v-if="isShowLiveScore && displayLiveScoreData" class="match-info-team-score">
                    {{ displayLiveScoreData.homeScore }}
                </div>
            </div>
            <div
                class="match-info-team-name JARVIS_MatchCard_Img_TeamNameB"
                v-bind:class="{
                    _favorite: info.isAwayFavorite,
                    _underdog: !info.isAwayFavorite,
                }"
            >
                <span>{{ awayTeamName }}</span>
                <div v-if="isShowLiveScore && displayLiveScoreData" class="match-info-team-score">
                    {{ displayLiveScoreData.awayScore }}
                </div>
            </div>
        </div>
        <div class="match-info-function">
            <div class="match-info-function-time">
                <div v-if="liveStatus.onLive" class="match-info-score">
                    <template v-if="isShowCountdown">
                        <time>
                            <Icons class="match-info-countdown-icon" iconName="systemIcon_timer" />
                            <span v-if="isShowCountdownHours">{{ countdownDisplay.hours }}:</span>
                            <span>{{ countdownDisplay.minutes }}</span>:<span>{{ countdownDisplay.seconds }}</span>
                        </time>
                    </template>
                    <template v-else-if="displayLiveScoreData?.liveScoreInfoDetail">
                        <time>
                            {{ displayLiveScoreData.liveScoreInfoDetail }}
                        </time>
                    </template>
                </div>
                <div v-else class="date _test">
                    {{ info.eventDate }}
                    <time v-if="info.showTime">{{ formatShowTime(info.showTime, info.showTimeType) }}</time>
                </div>
            </div>
            <div
                v-if="liveStatus.isToday"
                v-bind:class="['match-info-function-liveTag', { _selectedChannels: liveStatus.tvLive }]"
            >
                {{ getTranslationBySite('live').toUpperCase() }}
            </div>

            <SvgIcon
                v-bind:iconName="isStatisticsBtnShow ? 'sportIcon_statistics' : 'sportIcon_statistics_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-bind:iconName="isLiveStreamBtnShow ? 'sportIcon_liveTv' : 'sportIcon_liveTv_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-bind:iconName="isLiveCourtBtnShow ? 'sportIcon_liveCourt' : 'sportIcon_liveCourt_disabled'"
                class="match-info-function-item"
            />

            <SvgIcon
                v-if="!info.isLive && !isOutright && !isFromApp"
                iconName="systemIcon_calendar"
                class="match-info-function-item"
            />
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs } from 'vue';
    import { useLiveStream } from '@/components/liveStream/useLiveStream';
    import useMatchInfoScore from '@/components/match/matchInfo/useMatchInfoScore';
    import { useMatchFunction } from '@/components/matchFunction/useMatchFunction';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage, SportType } from '@/interface/enum';
    import { useApp } from '@/libraries/composables/useApp';
    import { useEventCountdown } from '@/libraries/composables/useEventCountdown';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { getLiveScoreDisplay } from '@/libraries/helpers/scoreHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useProductPlatformStore } from '@/stores/productPlatformStore';
    import { useRootStore } from '@/stores/rootStore';
    import Icons from '@/svg/Icons.vue';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'IndiaMatchInfo',
        components: {
            SvgIcon,
            Icons,
        },
        props: {
            info: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            marketPageType: {
                type: String as PropType<MarketPage>,
                default: MarketPage.Unknown,
            },
            matchName: {
                type: String,
                default: ' vs ',
            },
        },
        setup(props) {
            const { info } = toRefs(props);

            const liveStatus = computed(() => ({
                tvLive: info.value.isTvLive,
                sboLive: info.value.isSboLive,
                onLive: info.value.isLive,
                isToday: props.marketPageType === MarketPage.Today,
            }));

            const homeTeamName = computed(() => getTranslated(info.value.homeTeam?.teamName) || props.matchName.split(' vs ')[0]);

            const awayTeamName = computed(() => getTranslated(info.value.awayTeam?.teamName) || props.matchName.split(' vs ')[1]);

            const { isAsi, sportType } = storeToRefs(useRootStore());

            const { isLiveStreamPlayable } = useLiveStream(info);

            const { isComponentInPp } = storeToRefs(useProductPlatformStore());

            const { isStatisticsEnabled } = storeToRefs(useFeatureToggleStore());

            const isStatisticsBtnShow = computed(() => isStatisticsEnabled.value && info.value.hasStatistics);

            const isLiveCourtBtnShow = computed(() => info.value.liveCourtMatchId > 0);

            const isLiveStreamBtnShow = computed(() => isLiveStreamPlayable.value);

            const isOutright = computed(() => props.marketPageType === MarketPage.Outright);

            const { isShowCountdown, isShowCountdownHours, countdownDisplay } = useEventCountdown(info);

            const isEnabled = computed(() => info.value.isLive && [SportType.Soccer, SportType.Basketball, SportType.Football].includes(info.value.sportType));
            const { displayLiveScoreData } = useMatchInfoScore(info, isEnabled);
            const isShowLiveScore = computed(() => info.value.isLive && displayLiveScoreData.value && !isShowCountdown.value);

            const ticks = ref(0);
            const isRefreshing = ref(false);

            const refreshDuration = computed(() => (info.value.isLive ? 30 : 60));

            function tick() {
                ticks.value++;
                if (ticks.value >= refreshDuration.value) {
                    refresh();
                }
            }

            function refresh(force: boolean = false) {
                if (isRefreshing.value) return;

                ticks.value = 0;

                if (force) {
                    realRefresh();
                } else {
                    fakeRefresh();
                }
            }

            function fakeRefresh() {
                refreshAnimation();
            }

            function realRefresh() {
                refreshAnimation();

                if (isOutright.value) {
                    window.eventEmitter.emit('refreshMatch', info.value.leagueId, true, info.value.isLive);
                } else {
                    window.eventEmitter.emit('refreshMatch', info.value.eventId, true);
                }
            }

            function refreshAnimation() {
                isRefreshing.value = true;
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 1000);
            }

            const { goToMatchDetail } = useMatchFunction(info);

            const { isFromApp } = useApp();
            onMounted(() => {
                window.eventEmitter.on('tick', tick);
            });

            onUnmounted(() => {
                window.eventEmitter.off('tick', tick);
            });

            return {
                isLiveStreamPlayable,
                isAsi,
                sportType,
                isComponentInPp,
                getLiveScoreDisplay,
                formatShowTime,
                getTranslationBySite,
                isStatisticsBtnShow,
                isLiveCourtBtnShow,
                isLiveStreamBtnShow,
                goToMatchDetail,
                liveStatus,
                displayLiveScoreData,
                homeTeamName,
                awayTeamName,
                isOutright,
                isFromApp,
                isShowCountdown,
                isShowCountdownHours,
                countdownDisplay,
                isShowLiveScore,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/indiaSite/components/match/matchInfo";
</style>
